import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, CheckCircle2, XCircle, Clock, X } from "lucide-react";

interface ReviewResolutionsTabProps {
  data: any;
}

const getVoteResultConfig = (result: string) => {
  switch (result) {
    case 'adopted':
      return { icon: CheckCircle2, label: 'Adopted', variant: 'default' as const, color: 'text-green-600' };
    case 'rejected':
      return { icon: XCircle, label: 'Rejected', variant: 'destructive' as const, color: 'text-red-600' };
    case 'tabled':
      return { icon: Clock, label: 'Tabled', variant: 'secondary' as const, color: 'text-yellow-600' };
    case 'withdrawn':
      return { icon: X, label: 'Withdrawn', variant: 'secondary' as const, color: 'text-muted-foreground' };
    default:
      return { icon: Shield, label: 'Pending', variant: 'secondary' as const, color: 'text-muted-foreground' };
  }
};

export function ReviewResolutionsTab({ data }: ReviewResolutionsTabProps) {
  if (!data.resolutions_noted || data.resolutions_noted.length === 0) {
    return (
      <Card className="p-12 text-center">
        <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-muted-foreground">No resolutions voted on</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.resolutions_noted.map((resolution: any, index: number) => {
        const config = getVoteResultConfig(resolution.vote_result);
        const Icon = config.icon;
        
        return (
          <Card key={index} className="p-6">
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <h4 className="font-semibold text-lg">{resolution.title}</h4>
                <Badge variant={config.variant}>
                  <Icon className={`h-3 w-3 mr-1 ${config.color}`} />
                  {config.label}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Motion Made By:</span>
                  <p className="font-medium">{resolution.made_by || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Seconded By:</span>
                  <p className="font-medium">{resolution.seconded_by || 'N/A'}</p>
                </div>
              </div>

              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-muted-foreground">For:</span>
                  <span className="ml-1 font-medium text-green-600">{resolution.votes_for || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Against:</span>
                  <span className="ml-1 font-medium text-red-600">{resolution.votes_against || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Abstentions:</span>
                  <span className="ml-1 font-medium">{resolution.abstentions || 0}</span>
                </div>
              </div>

              {resolution.discussion_summary && (
                <div className="border-l-2 pl-3">
                  <span className="text-xs text-muted-foreground uppercase">Discussion</span>
                  <p className="text-sm text-muted-foreground mt-1">{resolution.discussion_summary}</p>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
